import React from "react";
import styled from "styled-components";

import ColorInput from "../../../common/ColorInput";
import {
  clampOptionalScale,
  contrastRatio,
  FONT_THEME_OPTIONS,
  PersonalizationDraft,
  resolvePreviewPalette,
  resolveStylePackPreview,
  STYLE_PACK_OPTIONS,
} from "./communityPersonalization";

const Panel = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(280px, 0.8fr);
  gap: var(--space-6);
  padding: var(--space-5);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-surface-subtle);

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Controls = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
  min-width: 0;
`;

const Group = styled.fieldset`
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
`;

const Legend = styled.legend`
  width: 100%;
  margin-bottom: var(--space-1);
  padding: 0;
  font-family: var(--font-sans);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const Helper = styled.p`
  margin: 0;
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  line-height: var(--line-height-normal);
  color: var(--color-text-secondary);
`;

const Fields = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-3);

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

const FieldShell = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
`;

const LabelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
`;

const Label = styled.label`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-secondary);
`;

const SupportBadge = styled.span<{ $future?: boolean }>`
  flex-shrink: 0;
  padding: 2px 7px;
  border-radius: var(--radius-pill);
  background: ${(p) =>
    p.$future ? "var(--color-warning-subtle)" : "var(--color-success-subtle)"};
  color: ${(p) => (p.$future ? "var(--color-warning)" : "var(--color-success)")};
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  white-space: nowrap;
`;

const Select = styled.select`
  width: 100%;
  height: 40px;
  padding: 0 var(--space-3);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  outline: none;
  background: var(--color-surface);
  color: var(--color-text);
  font-family: var(--font-sans);
  font-size: var(--font-size-base);

  &:focus-visible {
    border-color: var(--color-accent);
    box-shadow: var(--shadow-focus);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const ScaleInput = styled.input`
  width: 100%;
  height: 40px;
  padding: 0 var(--space-3);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  outline: none;
  background: var(--color-surface);
  color: var(--color-text);
  font-family: var(--font-mono);
  font-size: var(--font-size-base);

  &:focus-visible {
    border-color: var(--color-accent);
    box-shadow: var(--shadow-focus);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const PackCards = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(88px, 1fr));
  gap: var(--space-2);

  @media (max-width: 1080px) {
    grid-template-columns: repeat(3, minmax(100px, 1fr));
  }

  @media (max-width: 520px) {
    grid-template-columns: repeat(2, minmax(100px, 1fr));
  }
`;

const PackButton = styled.button<{ $selected: boolean }>`
  min-height: 88px;
  padding: var(--space-2);
  border: 1px solid
    ${(p) => (p.$selected ? "var(--color-accent)" : "var(--color-border)")};
  border-radius: var(--radius-md);
  background: var(--color-surface);
  color: var(--color-text);
  text-align: left;
  cursor: pointer;
  box-shadow: ${(p) => (p.$selected ? "var(--shadow-focus)" : "none")};

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const PackSample = styled.span<{
  $background: string;
  $border: string;
  $radius: number;
  $depth: "flat" | "shadow";
}>`
  display: block;
  height: 28px;
  margin-bottom: var(--space-2);
  border: 1px solid ${(p) => p.$border};
  border-radius: ${(p) => p.$radius}px;
  background: ${(p) => p.$background};
  box-shadow: ${(p) =>
    p.$depth === "shadow" ? "0 4px 9px rgba(0, 0, 0, 0.14)" : "none"};
`;

const PackName = styled.span`
  display: block;
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
`;

const PackMeta = styled.span`
  display: block;
  margin-top: 2px;
  color: var(--color-text-tertiary);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
`;

const PreviewColumn = styled.aside`
  min-width: 0;
`;

const PreviewSticky = styled.div`
  position: sticky;
  top: var(--space-5);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);

  @media (max-width: 900px) {
    position: static;
  }
`;

const PreviewHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-3);
`;

const PreviewTitle = styled.h4`
  margin: 0;
  font-family: var(--font-display);
  font-size: var(--font-size-lg);
  color: var(--color-text);
`;

const PreviewStatus = styled.span`
  color: var(--color-text-tertiary);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  text-align: right;
`;

const Device = styled.div<{ $background: string; $radius: number }>`
  min-height: 420px;
  padding: 22px 16px;
  overflow: hidden;
  border: 1px solid var(--color-border-strong);
  border-radius: ${(p) => Math.max(20, p.$radius + 10)}px;
  background: ${(p) => p.$background};
  box-shadow: var(--shadow-md);
`;

const DeviceBar = styled.div`
  width: 42px;
  height: 4px;
  margin: 0 auto 22px;
  border-radius: var(--radius-pill);
  background: rgba(90, 90, 90, 0.35);
`;

const PreviewEyebrow = styled.div<{ $color: string }>`
  margin-bottom: 6px;
  color: ${(p) => p.$color};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const PreviewHeading = styled.h5<{
  $color: string;
  $family: string;
  $scale: number;
}>`
  margin: 0 0 8px;
  color: ${(p) => p.$color};
  font-family: ${(p) => p.$family};
  font-size: ${(p) => 27 * p.$scale}px;
  line-height: 1.12;
`;

const PreviewBody = styled.p<{ $color: string; $scale: number }>`
  margin: 0;
  color: ${(p) => p.$color};
  font-family: var(--font-sans);
  font-size: ${(p) => 14 * p.$scale}px;
  line-height: 1.45;
`;

const PreviewCard = styled.div<{
  $surface: string;
  $border: string;
  $radius: number;
  $depth: "flat" | "shadow";
}>`
  margin-top: 20px;
  padding: 16px;
  border: 1px solid ${(p) => p.$border};
  border-radius: ${(p) => p.$radius}px;
  background: ${(p) => p.$surface};
  box-shadow: ${(p) =>
    p.$depth === "shadow" ? "0 8px 20px rgba(0, 0, 0, 0.12)" : "none"};
`;

const PreviewProgress = styled.div<{ $track: string }>`
  height: 7px;
  margin: 18px 0 10px;
  overflow: hidden;
  border-radius: var(--radius-pill);
  background: ${(p) => p.$track};
`;

const PreviewProgressFill = styled.div<{ $accent: string }>`
  width: 64%;
  height: 100%;
  background: ${(p) => p.$accent};
`;

const PreviewButton = styled.div<{
  $background: string;
  $color: string;
  $radius: number;
}>`
  margin-top: 16px;
  padding: 12px 14px;
  border-radius: ${(p) => p.$radius}px;
  background: ${(p) => p.$background};
  color: ${(p) => p.$color};
  font-family: var(--font-sans);
  font-size: 14px;
  font-weight: 700;
  text-align: center;
`;

const PaletteKey = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
`;

const PaletteChip = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--color-text-secondary);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);

  &::before {
    width: 10px;
    height: 10px;
    border: 1px solid var(--color-border-strong);
    border-radius: 50%;
    background: ${(p) => p.$color};
    content: "";
  }
`;

const Warning = styled.div`
  padding: var(--space-3);
  border: 1px solid var(--color-warning);
  border-radius: var(--radius-md);
  background: var(--color-warning-subtle);
  color: var(--color-text);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  line-height: var(--line-height-normal);
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
`;

const SecondaryButton = styled.button`
  min-height: 36px;
  padding: 0 var(--space-3);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  color: var(--color-text);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;

  &:hover:not(:disabled) {
    background: var(--color-surface-subtle);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

type Props = {
  values: PersonalizationDraft;
  savedValues: PersonalizationDraft;
  errors?: Partial<Record<keyof PersonalizationDraft, string>>;
  touched?: Partial<Record<keyof PersonalizationDraft, boolean>>;
  mode: string;
  isThemeBasePinned?: boolean;
  setFieldValue: (
    field: keyof PersonalizationDraft,
    value: string | number
  ) => void;
  handleChange: React.ChangeEventHandler<HTMLInputElement>;
  handleBlur: React.FocusEventHandler<HTMLInputElement>;
};

const PERSONALIZATION_FIELDS: Array<keyof PersonalizationDraft> = [
  "primaryColor",
  "secondaryColor",
  "accentColor",
  "backgroundColor",
  "surfaceColor",
  "surfaceElevatedColor",
  "surfaceBorderColor",
  "dividerColor",
  "textColor",
  "secondaryTextColor",
  "mutedTextColor",
  "onAccentColor",
  "fontTheme",
  "stylePack",
  "typographyScale",
  "radiusScale",
];

const OVERRIDE_COLOR_FIELDS: Array<keyof PersonalizationDraft> = [
  "accentColor",
  "backgroundColor",
  "surfaceColor",
  "surfaceElevatedColor",
  "surfaceBorderColor",
  "dividerColor",
  "textColor",
  "secondaryTextColor",
  "mutedTextColor",
  "onAccentColor",
];

const CommunityPersonalizationPanel = ({
  values,
  savedValues,
  errors = {},
  touched = {},
  mode,
  isThemeBasePinned = false,
  setFieldValue,
  handleChange,
  handleBlur,
}: Props): JSX.Element => {
  const disabled = mode === "detail";
  const previewValues = isThemeBasePinned
    ? { ...values, stylePack: "calmPower", fontTheme: "stressFluent" }
    : values;
  const pack = resolveStylePackPreview(
    previewValues.stylePack,
    previewValues.fontTheme
  );
  const palette = resolvePreviewPalette(previewValues);
  const font =
    FONT_THEME_OPTIONS.find(
      (option) => option.value === previewValues.fontTheme
    ) ||
    FONT_THEME_OPTIONS[0];
  const typographyScale =
    clampOptionalScale(values.typographyScale, 0.85, 1.25) || 1;
  const radiusScale = clampOptionalScale(values.radiusScale, 0, 2) ?? 1;
  const textOnBackground = contrastRatio(
    palette.textPrimary,
    palette.background
  );
  const textOnSurface = contrastRatio(palette.textPrimary, palette.surface);
  const buttonContrast = contrastRatio(palette.onAccent, palette.accent);
  const hasContrastWarning =
    textOnBackground < 4.5 || textOnSurface < 4.5 || buttonContrast < 4.5;
  const hasOverrides = Boolean(
    OVERRIDE_COLOR_FIELDS.some((field) => values[field]) ||
      values.typographyScale !== "" ||
      values.radiusScale !== ""
  );
  const personalizationChanged = PERSONALIZATION_FIELDS.some(
    (field) => values[field] !== savedValues[field]
  );

  const errorFor = (field: keyof PersonalizationDraft) =>
    touched[field] ? errors[field] : undefined;

  const resetOverrides = () => {
    if (
      !window.confirm(
        "Clear the optional palette and scale overrides? Primary and secondary colors, font, and style pack will stay unchanged. Nothing is saved until you save the community."
      )
    ) {
      return;
    }
    OVERRIDE_COLOR_FIELDS.forEach((field) => setFieldValue(field, ""));
    setFieldValue("typographyScale", "");
    setFieldValue("radiusScale", "");
  };

  const revertPreview = () => {
    if (
      !window.confirm(
        "Discard unsaved personalization changes? Other community fields will not be affected."
      )
    ) {
      return;
    }
    PERSONALIZATION_FIELDS.forEach((field) =>
      setFieldValue(field, savedValues[field] as string | number)
    );
  };

  const unknownFont =
    values.fontTheme &&
    !FONT_THEME_OPTIONS.some((option) => option.value === values.fontTheme);
  const unknownPack =
    values.stylePack &&
    !STYLE_PACK_OPTIONS.some((option) => option.key === values.stylePack);

  return (
    <Panel>
      <Controls>
        <Group>
          <Legend>Brand accents</Legend>
          <Helper>
            Primary and secondary remain the legacy brand colors. Accent is the
            semantic action color and falls back to primary when blank.
          </Helper>
          <Fields>
            <ColorInput
              id="primaryColor"
              name="primaryColor"
              label="Primary accent"
              value={values.primaryColor}
              error={errorFor("primaryColor")}
              disabled={disabled}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            <ColorInput
              id="secondaryColor"
              name="secondaryColor"
              label="Secondary accent"
              value={values.secondaryColor}
              error={errorFor("secondaryColor")}
              disabled={disabled}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            <ColorInput
              id="accentColor"
              name="accentColor"
              label="Action accent override"
              placeholder="Falls back to primary accent"
              value={values.accentColor}
              error={errorFor("accentColor")}
              disabled={disabled}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            <ColorInput
              id="onAccentColor"
              name="onAccentColor"
              label="Text on action accent"
              placeholder={`${pack.palette.onPrimary} from ${pack.label}`}
              value={values.onAccentColor}
              error={errorFor("onAccentColor")}
              disabled={disabled}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </Fields>
        </Group>

        <Group>
          <Legend>Typography</Legend>
          <Helper>
            Font families are bundled per white-label build. Typography scale
            is a runtime override and is clamped from 0.85 to 1.25.
          </Helper>
          {isThemeBasePinned ? (
            <Warning>
              Return Daily keeps its bundled Stress Fluent font and Calm Power
              base pack. New palette and scale overrides still layer over that
              pinned design system.
            </Warning>
          ) : null}
          <FieldShell>
            <LabelRow>
              <Label htmlFor="fontTheme">Font theme</Label>
              <SupportBadge $future>Build required</SupportBadge>
            </LabelRow>
            <Select
              id="fontTheme"
              name="fontTheme"
              value={values.fontTheme}
              disabled={disabled || isThemeBasePinned}
              onChange={(event) =>
                setFieldValue("fontTheme", event.target.value)
              }
            >
              <option value="">Brand build default</option>
              {unknownFont ? (
                <option value={values.fontTheme}>
                  Saved value: {values.fontTheme}
                </option>
              ) : null}
              {FONT_THEME_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}: {option.hint}
                </option>
              ))}
            </Select>
          </FieldShell>
          <FieldShell>
            <LabelRow>
              <Label htmlFor="typographyScale">Typography scale</Label>
              <SupportBadge>Runtime</SupportBadge>
            </LabelRow>
            <ScaleInput
              id="typographyScale"
              name="typographyScale"
              type="number"
              min="0.85"
              max="1.25"
              step="0.05"
              value={values.typographyScale}
              disabled={disabled}
              placeholder="1.00"
              aria-describedby="typography-scale-help"
              onChange={(event) =>
                setFieldValue(
                  "typographyScale",
                  event.target.value === "" ? "" : Number(event.target.value)
                )
              }
            />
            <Helper id="typography-scale-help">
              1.00 uses the pack scale. Smaller is denser and larger is more
              spacious.
            </Helper>
          </FieldShell>
        </Group>

        <Group>
          <Legend>Shape and depth</Legend>
          <Helper>
            Style packs are ThePath's supported shape control. Each pack owns
            its base radii, borders, depth, icon treatment, and motion. Radius
            scale adjusts the pack's corners at runtime from square to twice as
            rounded.
          </Helper>
          {unknownPack ? (
            <FieldShell>
              <Label htmlFor="stylePack">Saved style pack</Label>
              <Select
                id="stylePack"
                value={values.stylePack}
                disabled={disabled || isThemeBasePinned}
                onChange={(event) =>
                  setFieldValue("stylePack", event.target.value)
                }
              >
                <option value={values.stylePack}>{values.stylePack}</option>
                <option value="">Automatic</option>
                {STYLE_PACK_OPTIONS.map((option) => (
                  <option key={option.key} value={option.key}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </FieldShell>
          ) : null}
          <PackCards role="radiogroup" aria-label="Style pack">
            {STYLE_PACK_OPTIONS.map((option) => (
              <PackButton
                key={option.key}
                type="button"
                role="radio"
                aria-checked={previewValues.stylePack === option.key}
                $selected={previewValues.stylePack === option.key}
                disabled={disabled || isThemeBasePinned}
                title={option.hint}
                onClick={() => setFieldValue("stylePack", option.key)}
              >
                <PackSample
                  $background={option.palette.surface}
                  $border={option.palette.surfaceBorder}
                  $radius={option.shape.radiusMd}
                  $depth={option.depth}
                />
                <PackName>{option.label}</PackName>
                <PackMeta>
                  {option.shape.radiusMd}px · {option.depth}
                </PackMeta>
              </PackButton>
            ))}
          </PackCards>
          <SecondaryButton
            type="button"
            disabled={disabled || isThemeBasePinned || !values.stylePack}
            onClick={() => setFieldValue("stylePack", "")}
          >
            Use automatic pack
          </SecondaryButton>
          <FieldShell>
            <LabelRow>
              <Label htmlFor="radiusScale">Radius scale</Label>
              <SupportBadge>Runtime</SupportBadge>
            </LabelRow>
            <ScaleInput
              id="radiusScale"
              name="radiusScale"
              type="number"
              min="0"
              max="2"
              step="0.1"
              value={values.radiusScale}
              disabled={disabled}
              placeholder="1.00"
              aria-describedby="radius-scale-help"
              onChange={(event) =>
                setFieldValue(
                  "radiusScale",
                  event.target.value === "" ? "" : Number(event.target.value)
                )
              }
            />
            <Helper id="radius-scale-help">
              0 makes pack corners square, 1 uses the pack, and 2 doubles its
              radii.
            </Helper>
          </FieldShell>
        </Group>

        <Group>
          <Legend>Semantic palette overrides</Legend>
          <Helper>
            Each optional color layers over the selected style pack. Use full
            six-digit hex values. Blank fields inherit the pack. A custom
            surface also seeds the elevated surface when that field is blank.
          </Helper>
          <Fields>
            <ColorInput
              id="backgroundColor"
              name="backgroundColor"
              label="App background"
              placeholder={`${pack.palette.background} from ${pack.label}`}
              value={values.backgroundColor}
              error={errorFor("backgroundColor")}
              disabled={disabled}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            <ColorInput
              id="surfaceColor"
              name="surfaceColor"
              label="Card surface"
              placeholder={`${pack.palette.surface} from ${pack.label}`}
              value={values.surfaceColor}
              error={errorFor("surfaceColor")}
              disabled={disabled}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            <ColorInput
              id="surfaceElevatedColor"
              name="surfaceElevatedColor"
              label="Elevated surface"
              placeholder="Falls back to card surface"
              value={values.surfaceElevatedColor}
              error={errorFor("surfaceElevatedColor")}
              disabled={disabled}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            <ColorInput
              id="surfaceBorderColor"
              name="surfaceBorderColor"
              label="Surface border"
              placeholder={`${pack.palette.surfaceBorder} from ${pack.label}`}
              value={values.surfaceBorderColor}
              error={errorFor("surfaceBorderColor")}
              disabled={disabled}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            <ColorInput
              id="dividerColor"
              name="dividerColor"
              label="Divider"
              placeholder={`${pack.palette.divider} from ${pack.label}`}
              value={values.dividerColor}
              error={errorFor("dividerColor")}
              disabled={disabled}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            <ColorInput
              id="textColor"
              name="textColor"
              label="Primary text"
              placeholder={`${pack.palette.textPrimary} from ${pack.label}`}
              value={values.textColor}
              error={errorFor("textColor")}
              disabled={disabled}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            <ColorInput
              id="secondaryTextColor"
              name="secondaryTextColor"
              label="Secondary text"
              placeholder={`${pack.palette.textSecondary} from ${pack.label}`}
              value={values.secondaryTextColor}
              error={errorFor("secondaryTextColor")}
              disabled={disabled}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            <ColorInput
              id="mutedTextColor"
              name="mutedTextColor"
              label="Muted text"
              placeholder={`${pack.palette.textMuted} from ${pack.label}`}
              value={values.mutedTextColor}
              error={errorFor("mutedTextColor")}
              disabled={disabled}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </Fields>
          <LabelRow>
            <Helper>
              The mobile runtime ignores invalid optional colors and safely
              falls back through the semantic theme chain.
            </Helper>
            <SupportBadge>Runtime</SupportBadge>
          </LabelRow>
        </Group>

        {!disabled ? (
          <Actions>
            <SecondaryButton
              type="button"
              disabled={!hasOverrides}
              onClick={resetOverrides}
            >
              Reset overrides to base
            </SecondaryButton>
            <SecondaryButton
              type="button"
              disabled={!personalizationChanged}
              onClick={revertPreview}
            >
              Revert unsaved preview
            </SecondaryButton>
          </Actions>
        ) : null}
        <Helper role="status" aria-live="polite">
          Preview changes are local until the Community save action is used.
        </Helper>
      </Controls>

      <PreviewColumn aria-label="Community theme preview">
        <PreviewSticky>
          <PreviewHeader>
            <PreviewTitle>Member preview</PreviewTitle>
            <PreviewStatus>
              {pack.label}
              <br />
              {font.label}
            </PreviewStatus>
          </PreviewHeader>
          <Device
            $background={palette.background}
            $radius={pack.shape.radiusLg * radiusScale}
          >
            <DeviceBar />
            <PreviewEyebrow $color={palette.secondary}>
              Today's practice
            </PreviewEyebrow>
            <PreviewHeading
              $color={palette.textPrimary}
              $family={font.previewFamily}
              $scale={typographyScale}
            >
              A calmer path forward
            </PreviewHeading>
            <PreviewBody
              $color={palette.textSecondary}
              $scale={typographyScale}
            >
              Continue where you left off and build a steady rhythm this week.
            </PreviewBody>
            <PreviewCard
              $surface={palette.surface}
              $border={palette.surfaceBorder}
              $radius={pack.shape.radiusMd * radiusScale}
              $depth={pack.depth}
            >
              <PreviewEyebrow $color={palette.secondary}>
                Day 4 of 7
              </PreviewEyebrow>
              <PreviewHeading
                $color={palette.textPrimary}
                $family={font.previewFamily}
                $scale={typographyScale * 0.74}
              >
                Your next small win
              </PreviewHeading>
              <PreviewBody
                $color={palette.textSecondary}
                $scale={typographyScale}
              >
                Ten focused minutes is enough to keep the promise you made to
                yourself.
              </PreviewBody>
              <PreviewProgress $track={palette.divider}>
                <PreviewProgressFill $accent={palette.secondary} />
              </PreviewProgress>
              <PreviewButton
                $background={palette.accent}
                $color={palette.onAccent}
                $radius={pack.shape.buttonRadius * radiusScale}
              >
                Continue practice
              </PreviewButton>
            </PreviewCard>
          </Device>
          <PaletteKey aria-label="Resolved preview palette">
            <PaletteChip $color={palette.background}>Background</PaletteChip>
            <PaletteChip $color={palette.surface}>Surface</PaletteChip>
            <PaletteChip $color={palette.surfaceElevated}>Elevated</PaletteChip>
            <PaletteChip $color={palette.textPrimary}>Text</PaletteChip>
            <PaletteChip $color={palette.textMuted}>Muted</PaletteChip>
            <PaletteChip $color={palette.accent}>Accent</PaletteChip>
            <PaletteChip $color={palette.secondary}>Secondary</PaletteChip>
          </PaletteKey>
          {hasContrastWarning ? (
            <Warning role="alert">
              This preview has a text pairing below WCAG AA contrast. Adjust the
              text, background, surface, or primary accent before saving.
            </Warning>
          ) : null}
        </PreviewSticky>
      </PreviewColumn>
    </Panel>
  );
};

export default CommunityPersonalizationPanel;
